import React, { Component, ReactNode } from 'react';
import { ErrorType, getErrorType, reloadOnError } from './util';
import ErrorFallback from './ErrorFallback';

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  errorType: ErrorType | null;
  error: Error | null;
}

class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      errorType: null,
      error: null,
    };
  }

  // reset state
  resetErrorState = (): void => {
    this.setState({
      hasError: false,
      errorType: null,
      error: null,
    });
  };

  // set state
  setErrorState = (hasError: boolean, errorType: ErrorType | null, error: Error | null): void => {
    this.setState({
      hasError,
      errorType,
      error,
    });
  };

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
    const errorType = getErrorType(error);
    console.error('ErrorBoundary caught an error:', errorType, error, errorInfo);

    if (errorType === ErrorType.CHUNK_LOAD_ERROR) {
      reloadOnError();
    }

    this.setErrorState(true, errorType, error);
  }

  render(): ReactNode {
    const { hasError, errorType, error } = this.state;

    if (hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      if (errorType === ErrorType.CHUNK_LOAD_ERROR || errorType === ErrorType.SERVER_ERROR) {
        return (
          <ErrorFallback
            error={error}
            isChunkError={errorType === ErrorType.CHUNK_LOAD_ERROR}
            onReload={this.resetErrorState}
          />
        );
      }

      return null;
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
